import React, { ChangeEvent, useEffect, useState } from 'react';
import { Namespace } from '../../../../translation/namespace';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import {
  DateRangeWithIsAllDaySwitch,
  Input,
  Option,
  RequiredHelpPhrase,
  Row,
  Select,
  Switch,
  Textarea,
  useForm,
} from '../../../../_lib/Form2';
import { AnnouncementCreateOrUpdateForm } from './AnnouncementCreateOrUpdate';
import { GroupPreviewMultiSelectComboBoxForForm2 } from '../../../../components/molecules/Form/components/ComboBox';
import { HiddenInput } from '../../../../_lib/Form2/elements/HiddenInput/HiddenInput';

export default function AnnouncementCreateOrUpdateTemplate({
  form,
}: Readonly<{
  form: AnnouncementCreateOrUpdateForm;
}>) {
  const { t } = useSonTranslation('Announcement');

  const { params } = useForm({
    form,
  });

  const announcementIdParam = params.findByName('announcementId');
  const titleParam = params.findByName('title');
  const categoryIdParam = params.findByName('categoryId');
  const linkedPageTitleParam = params.findByName('linkedPageTitle');
  const sendToAllParam = params.findByName('sendToAll');
  const mandatoryParam = params.findByName('mandatory');
  const recipientParam = params.findByName('recipient');
  const textParam = params.findByName('text');
  const dateRangeParam = params.findByName('daterange');

  const [recipientComboboxDisabled, setRecipientComboboxDisabled] = useState(false);

  const idSuffix = 'announcement';

  // When pre-filling the form, we need to check the dependency for sendToAll and the recipient combobox visibility.
  useEffect(() => {
    if (sendToAllParam && sendToAllParam.checked !== undefined) {
      setRecipientComboboxDisabled(sendToAllParam.checked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <RequiredHelpPhrase />
      <Row>
        <Input param={titleParam} key={`${titleParam?.name}-${idSuffix}`} label={t('form.title')} />
      </Row>
      {!!categoryIdParam?.options.length && categoryIdParam?.options.length > 0 && (
        <Row>
          <Select param={categoryIdParam} label={t(`form.category`)}>
            <Option key="default" value="">
              {t(`${Namespace.phrases}:selectPlaceholderOptionNotRequiredLabel`)}
            </Option>
            {categoryIdParam.options.map((option: { value: string; text: string }) => (
              <Option key={option.value} value={option.value}>
                {option.text}
              </Option>
            ))}
          </Select>
        </Row>
      )}
      {linkedPageTitleParam && (
        <Row>
          <Input
            param={linkedPageTitleParam}
            key={`${linkedPageTitleParam.name}-${idSuffix}`}
            label={t('form.linkedPageTitle')}
          />
        </Row>
      )}
      {mandatoryParam && (
        <Row key={`${mandatoryParam.name}-${idSuffix}`}>
          <Switch
            param={mandatoryParam}
            key={`${mandatoryParam.name}-checkbox}`}
            label={t('mandatory.text')}
            helpText={t('form.mandatoryLabelHelpText')}
            onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
              (mandatoryParam as HTMLInputElement).checked = target.checked;
            }}
          />
        </Row>
      )}
      {sendToAllParam && (
        <Row key={`${sendToAllParam.name}-${idSuffix}`}>
          <Switch
            param={sendToAllParam}
            key={`${sendToAllParam.name}-checkbox}`}
            label={t('form.sendToAll')}
            onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
              (sendToAllParam as HTMLInputElement).checked = target.checked;
              setRecipientComboboxDisabled(target.checked);
            }}
          />
        </Row>
      )}
      {!recipientComboboxDisabled && recipientParam && (
        <Row>
          <GroupPreviewMultiSelectComboBoxForForm2
            name={recipientParam.name}
            value={recipientParam.value}
            suggestUrl={recipientParam.comboBoxUrl.url}
            labelTitle={t('form.groups.label')}
            labelHelpText={t('form.groups.labelHelpText')}
            required={recipientParam.required}
            hasReadProtectedItems={recipientParam.hasReadProtectedItems}
          />
        </Row>
      )}
      <Row>
        <Textarea param={textParam} key={`${textParam?.name}-${idSuffix}`} autoExpand label={t('form.textAreaTitle')} />
      </Row>
      {dateRangeParam && (
        <DateRangeWithIsAllDaySwitch
          dateRangeParam={dateRangeParam}
          labelSwitch={t('form.daterange.isAllDay')}
          labelStart={t('form.daterange.from')}
          labelEnd={t('form.daterange.to')}
        />
      )}
      {announcementIdParam && <HiddenInput {...announcementIdParam} />}
    </>
  );
}
